import { NavLink, useNavigate, useSearchParams } from "react-router-dom";
import { useCallback, useEffect, useState } from "react";
import Swal from "sweetalert2";
import { apiRequest, formatCurrency, getImageUrl } from "../../../core/common/api";
import AppImage from "../../../shared/AppImage";
import MetricCard from "../../../shared/MetricCard";
import PaginationBar from "../../../shared/PaginationBar";

const ProductList = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [products, setProducts] = useState<any[]>([]);
  const [summary, setSummary] = useState<any>({});
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    totalPages: 1,
    total: 0,
  });
  const initialTag = ["featured", "bestSeller", "newArrival"].includes(
    searchParams.get("tag") || "",
  )
    ? (searchParams.get("tag") as string)
    : "all";
  const [filters, setFilters] = useState({
    search: "",
    category: "all",
    isActive: "all",
    stock: "all",
    tag: initialTag,
    sort: "newest",
  });
  const [isLoading, setIsLoading] = useState(false);

  const fetchProducts = useCallback(
    async (page = 1) => {
      setIsLoading(true);
      try {
        const { search, ...restFilters } = filters;
        const params = new URLSearchParams({
          ...restFilters,
          keyword: search,
          page: String(page),
          limit: String(pagination.limit),
        });
        const result = await apiRequest(
          `/products?${params.toString()}`,
          "GET",
        );
        if (result.success) {
          setProducts(result.products || []);
          setSummary(result.summary || {});
          setPagination({
            page: result.pagination?.page || page,
            limit: result.pagination?.limit || pagination.limit,
            totalPages: result.pagination?.totalPages || 1,
            total: result.pagination?.total ?? result.summary?.totalProducts ?? (result.products || []).length,
          });
        }
      } catch (error: any) {
        Swal.fire({
          title: error.message || "Unable to load products",
          icon: "error",
        });
      } finally {
        setIsLoading(false);
      }
    },
    [filters, pagination.limit],
  );

  const deleteProduct = async (id: string) => {
    const confirm = await Swal.fire({
      title: "Delete product?",
      icon: "warning",
      showCancelButton: true,
    });
    if (!confirm.isConfirmed) return;

    try {
      const result = await apiRequest(`/products/${id}`, "DELETE");
      if (result.success) fetchProducts(pagination.page);
    } catch (error: any) {
      Swal.fire({ title: error.message || "Delete failed", icon: "error" });
    }
  };

  const editProduct = (id: string) => {
    navigate(`/products/edit/${id}`);
  };

  useEffect(() => {
    fetchProducts(1);
  }, [fetchProducts]);

  const metrics = [
    {
      label: "Total Products",
      value: summary.totalProducts ?? 0,
      icon: "🛍️",
      tone: "orange",
    },
    {
      label: "Active Products",
      value: summary.activeProducts ?? 0,
      icon: "✅",
      tone: "green",
    },
    {
      label: "Featured",
      value: summary.featuredProducts ?? 0,
      icon: "⭐",
      tone: "blue",
    },
    {
      label: "Best Sellers",
      value: summary.bestSellers ?? 0,
      icon: "🔥",
      tone: "purple",
    },
    {
      label: "New Arrivals",
      value: summary.newArrivals ?? 0,
      icon: "✨",
      tone: "pink",
    },
  ];

  return (
    <>
      <div className="page-hdr">
        <div className="page-hdr-left">
          <div className="page-eyebrow">Overview</div>
          <h1 className="page-title">
            All <em>Products</em>
          </h1>
          <div className="page-sub">
            Track all product listings, approvals and recent platform activity.
          </div>
        </div>
        <div className="page-hdr-actions">
          <NavLink to="/add-products" className="btn-sm btn-primary">
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M12 5v14"></path>
              <path d="M5 12h14"></path>
            </svg>{" "}
            Add Product
          </NavLink>
        </div>
      </div>

      <section className="metrics-grid cols-5">
        {metrics.map((metric) => (
          <MetricCard key={metric.label} metric={metric} />
        ))}
      </section>

      <div className="toolbar">
        <div className="toolbar-search">
          <svg viewBox="0 0 24 24">
            <circle cx="11" cy="11" r="8" />
            <path d="m21 21-4.35-4.35" />
          </svg>
          <input
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            onKeyDown={(e) => {
              if (e.key === "Enter") fetchProducts(1);
            }}
            placeholder="Search products..."
          />
        </div>

        <select
          className="filter-select"
          value={filters.category}
          onChange={(e) => setFilters({ ...filters, category: e.target.value })}
        >
          <option value="all">All Categories</option>
          {(summary.categories || []).map((category: string) => (
            <option value={category} key={category}>
              {category}
            </option>
          ))}
        </select>

        <select
          className="filter-select"
          value={filters.isActive}
          onChange={(e) => setFilters({ ...filters, isActive: e.target.value })}
        >
          <option value="all">All Status</option>
          <option value="true">Active</option>
          <option value="false">Inactive</option>
        </select>

        <select
          className="filter-select"
          value={filters.stock}
          onChange={(e) => setFilters({ ...filters, stock: e.target.value })}
        >
          <option value="all">All Stock</option>
          <option value="in">In Stock</option>
          <option value="low">Low Stock</option>
          <option value="out">Out of Stock</option>
        </select>

        <select
          className="filter-select"
          value={filters.tag}
          onChange={(e) => setFilters({ ...filters, tag: e.target.value })}
        >
          <option value="all">All Tags</option>
          <option value="featured">Featured</option>
          <option value="bestSeller">Best Sellers</option>
          <option value="newArrival">New Arrivals</option>
        </select>

        <select
          className="filter-select"
          value={filters.sort}
          onChange={(e) => setFilters({ ...filters, sort: e.target.value })}
        >
          <option value="newest">Newest First</option>
          <option value="oldest">Oldest First</option>
          <option value="priceHigh">Price High</option>
          <option value="priceLow">Price Low</option>
          <option value="stockLow">Stock Low</option>
        </select>

        <div className="toolbar-right">
          <button className="btn-sm btn-outline">
            <svg viewBox="0 0 24 24">
              <polyline points="1 4 1 10 7 10" />
              <path d="M3.51 15a9 9 0 101.85-6.86L1 10" />
            </svg>
            Reset
          </button>
        </div>
      </div>

      <section className="table-card">
        <div className="table-head">
          <h2>Products List</h2>
          <p>Manage Products</p>
        </div>

        <div className="table-scroll">
        <table>
          <thead>
            <tr>
              <th>Product</th>
              <th>Category</th>
              <th>Price</th>
              <th>Selling Price</th>
              <th>Stock</th>
              <th>Tags</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={8} className="empty-state">
                  Loading products...
                </td>
              </tr>
            ) : products.length === 0 ? (
              <tr>
                <td colSpan={8} className="empty-state">
                  No products found.
                </td>
              </tr>
            ) : (
              products.map((product) => (
                <tr key={product._id}>
                  <td>
                    <div className="img-name-cell">
                      <AppImage
                        src={getImageUrl(product.images?.[0])}
                        alt={product.name}
                        crossOrigin="anonymous"
                      />
                      <div>
                        <strong>{product.name}</strong>
                        <small>{product.shortDescription}</small>
                      </div>
                    </div>
                  </td>
                  <td>{product.category || "-"}</td>
                  <td>{formatCurrency(product.price)}</td>
                  <td>
                    <strong>{formatCurrency(product.sellingPrice)}</strong>
                  </td>
                  <td>{product.stock}</td>
                  <td>
                    <div className="actions-btns" style={{ flexWrap: "wrap", gap: 4 }}>
                      {product.isFeatured ? (
                        <span className="status-badge success">Featured</span>
                      ) : null}
                      {product.isBestSeller ? (
                        <span className="status-badge warning">Best Seller</span>
                      ) : null}
                      {product.isNewArrival ? (
                        <span className="status-badge muted">New Arrival</span>
                      ) : null}
                      {!product.isFeatured &&
                      !product.isBestSeller &&
                      !product.isNewArrival ? (
                        <span className="table-muted">—</span>
                      ) : null}
                    </div>
                  </td>
                  <td>
                    <span
                      className={`status-badge ${product.isActive ? "success" : "danger"}`}
                    >
                      {product.isActive ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td>
                    <div className="actions-btns">
                      <button
                        type="button"
                        onClick={() => editProduct(product._id)}
                        title="Edit product"
                        className="action-button edit"
                      >
                        ✏️
                      </button>
                      <button
                        type="button"
                        onClick={() => deleteProduct(product._id)}
                        title="Delete product"
                        className="action-button delete"
                      >
                        🗑️
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        </div>

        <PaginationBar
          page={pagination.page}
          limit={pagination.limit}
          total={pagination.total}
          totalPages={pagination.totalPages}
          itemLabel="products"
          onPageChange={fetchProducts}
          onLimitChange={(limit) =>
            setPagination((current) => ({ ...current, page: 1, limit }))
          }
        />
      </section>
    </>
  );
};

export default ProductList;
